/**
 * Shared MSAL instance — single PublicClientApplication for the entire app.
 *
 * Both AuthContext and authenticatedFetch import from here so there is exactly
 * one MSAL instance (one cache partition, one initialize() lifecycle).
 */

import { PublicClientApplication } from '@azure/msal-browser';

export const loginScopes = ['openid', 'profile', 'email', 'User.Read', 'Files.Read'];

export const entraTenantId =
  (import.meta as any).env?.VITE_ENTRA_TENANT_ID ?? '18a2c300-b493-4837-a293-015508c28170';
export const entraClientId =
  (import.meta as any).env?.VITE_ENTRA_CLIENT_ID ?? 'f7e0e8e5-6cc3-4908-8031-7754c87ac987';
export const entraRedirectUri =
  (import.meta as any).env?.VITE_ENTRA_REDIRECT_URI ??
  (typeof window !== 'undefined'
    ? window.location.origin
    : 'https://ally-v2-vnet.jollywave-0e412d60.westus.azurecontainerapps.io');

const msalConfig = {
  auth: {
    clientId: entraClientId,
    authority: `https://login.microsoftonline.com/${entraTenantId}`,
    redirectUri: entraRedirectUri,
  },
  cache: {
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: false,
  },
};

/** The single MSAL instance for the entire application. */
export const msalInstance = new PublicClientApplication(msalConfig);

let _initialized = false;
let _initPromise: Promise<void> | null = null;

/**
 * Ensure msalInstance.initialize() is called exactly once.  Safe to call from
 * multiple call-sites concurrently — subsequent calls await the same promise.
 */
export async function ensureMsalInitialized(): Promise<void> {
  if (_initialized) return;
  if (!_initPromise) {
    _initPromise = msalInstance.initialize().then(() => {
      _initialized = true;
    });
  }
  return _initPromise;
}
